import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';
import '../styles/Cart.css';

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, clearCart, getCartTotal } = useCart();
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderSent, setOrderSent] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '' });
  const [formErrors, setFormErrors] = useState({});

  const subtotal = getCartTotal();
  const total = subtotal;
  const hasStudioItem = cartItems.some(item => item.category === 'Studio');

  const handleQuantityChange = (productId, newQuantity) => {
    updateQuantity(productId, newQuantity);
  };

  const handleRemove = (productId) => {
    removeFromCart(productId);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Enter a valid email';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePlaceOrderClick = () => {
    if (cartItems.length === 0) return;
    setShowOrderForm(true);
    setOrderSent(false);
    setFormErrors({});
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    let phoneNumber = process.env.REACT_APP_WHATSAPP || process.env.REACT_APP_PHONE_NUMBER || '';
    phoneNumber = String(phoneNumber).replace(/[^\d]/g, '');

    let message = `*New Order Request*\n\n`;
    message += `*Customer Details:*\n`;
    message += `Name: ${formData.name.trim()}\n`;
    message += `Phone: ${formData.phone.trim()}\n`;
    message += `Email: ${formData.email.trim()}\n\n`;
    message += `*Cart Items:*\n`;
    cartItems.forEach((item, index) => {
      const itemPrice = typeof item.price === 'number'
        ? item.price
        : parseFloat(String(item.price).replace(/[^\d.]/g, '')) || 0;
      const itemTotal = itemPrice * item.quantity;
      message += `${index + 1}. *${item.name}*\n`;
      message += `   Category: ${item.category}\n`;
      message += `   Quantity: ${item.quantity}\n`;
      message += `   Price: ${item.category !== 'Studio' ? '₹' : ''}${itemPrice}/day\n`;
      message += `   Total: ${item.category !== 'Studio' ? '₹' : ''}${itemTotal.toFixed(2)}\n\n`;
    });
    message += `*Order Total: ${!hasStudioItem ? '₹' : ''}${total.toFixed(2)}*\n\n`;
    message += `Please confirm availability and booking.`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

    clearCart();
    setShowOrderForm(false);
    setOrderSent(true);
    setFormData({ name: '', phone: '', email: '' });
    setFormErrors({});
  };

  const handleCloseForm = () => {
    setShowOrderForm(false);
    setFormErrors({});
  };

  return (
    <>
      <SEO
        title="Shopping Cart | BookMyCamera"
        description="View your selected camera equipment and photography services in your cart."
        noindex={true}
      />
      <div className="cart-container">
        <div className="cart-header">
          <h1>Shopping Cart</h1>
          <p>Review your items and place your order</p>
        </div>

        {orderSent && (
          <div className="cart-order-success">
            <div className="order-success-icon">✓</div>
            <h3>Order submitted!</h3>
            <p>A new tab opened with your order details. Send the message on WhatsApp to confirm your order with us.</p>
          </div>
        )}

        <div className="cart-content">
          <div className="cart-items-section">
            <div className="cart-items-header">
              <h2>Cart Items ({cartItems.length})</h2>
              {cartItems.length > 0 && (
                <button className="clear-cart-btn" onClick={clearCart}>
                  Clear Cart
                </button>
              )}
            </div>

            {cartItems.length === 0 && !showOrderForm ? (
              <div className="empty-cart">
                <div className="empty-icon">🛒</div>
                <h3>Your cart is empty</h3>
                <p>Add some items to get started!</p>
                <a href="/products" className="continue-shopping-btn">Continue Shopping</a>
              </div>
            ) : (
              <div className="cart-items">
                {cartItems.map(item => {
                  const itemPrice = typeof item.price === 'number'
                    ? item.price
                    : parseFloat(String(item.price).replace(/[^\d.]/g, '')) || 0;
                  const hasDiscount = item.discount && item.discount > 0;
                  return (
                    <div key={item.id} className="cart-item">
                      <div className="item-icon">
                        <img src={item.image} alt={item.name} className="cart-item-img" />
                        {hasDiscount && (
                          <div className="cart-discount-badge">-{item.discount}%</div>
                        )}
                      </div>
                      <div className="item-details">
                        <h3>{item.name}</h3>
                        <p className="item-category">{item.category}</p>
                        <div className="item-price-section">
                          {hasDiscount && item.originalPrice && (
                            <span className="item-original-price">{item.category !== 'Studio' ? '₹' : ''}{item.originalPrice}</span>
                          )}
                        <p className="item-price">{item.category !== 'Studio' ? '₹' : ''}{itemPrice}/day</p>
                      </div>
                    </div>
                      <div className="item-quantity">
                        <button className="qty-btn" onClick={() => handleQuantityChange(item.id, item.quantity - 1)}>−</button>
                        <span>{item.quantity}</span>
                        <button className="qty-btn" onClick={() => handleQuantityChange(item.id, item.quantity + 1)}>+</button>
                      </div>
                      <div className="item-total">
                        {item.category !== 'Studio' ? '₹' : ''}{(itemPrice * item.quantity).toFixed(2)}
                      </div>
                      <button className="remove-btn" onClick={() => handleRemove(item.id)} title="Remove from cart">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="cart-summary">
            <h2>Order Summary</h2>
            <div className="summary-details">
              <div className="summary-row total-row">
                <span>Total</span>
                <span>{!hasStudioItem ? '₹' : ''}{total.toFixed(2)}</span>
              </div>
            </div>

            <button
              className="checkout-btn"
              onClick={handlePlaceOrderClick}
              disabled={cartItems.length === 0}
            >
              Place Order
            </button>

            <p className="cart-terms-note">
              By placing your order you agree to our{' '}
              <Link to="/terms" className="cart-terms-link">Terms and Conditions</Link>.
            </p>

            {showOrderForm && (
              <div className="cart-order-form-overlay">
                <div className="cart-order-form-box">
                  <h3>Your details</h3>
                  <p className="cart-order-form-desc">Fill in your details to send the order to us on WhatsApp.</p>
                  <form onSubmit={handleFormSubmit} className="cart-order-form">
                    <div className="cart-form-group">
                      <label htmlFor="order-name">Name *</label>
                      <input
                        id="order-name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your name"
                        className={formErrors.name ? 'error' : ''}
                      />
                      {formErrors.name && <span className="cart-form-error">{formErrors.name}</span>}
                    </div>
                    <div className="cart-form-group">
                      <label htmlFor="order-phone">Phone number *</label>
                      <input
                        id="order-phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="e.g. 9876543210"
                        className={formErrors.phone ? 'error' : ''}
                      />
                      {formErrors.phone && <span className="cart-form-error">{formErrors.phone}</span>}
                    </div>
                    <div className="cart-form-group">
                      <label htmlFor="order-email">Email *</label>
                      <input
                        id="order-email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="your@email.com"
                        className={formErrors.email ? 'error' : ''}
                      />
                      {formErrors.email && <span className="cart-form-error">{formErrors.email}</span>}
                    </div>
                    <p className="cart-order-form-terms">
                      By submitting you agree to our{' '}
                      <Link to="/terms" className="cart-terms-link">
                        Terms and Conditions
                      </Link>.
                    </p>
                    <div className="cart-form-actions">
                      <button type="button" className="cart-form-cancel" onClick={handleCloseForm}>
                        Cancel
                      </button>
                      <button type="submit" className="cart-form-submit">
                        Submit & Send to WhatsApp
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="promo-code">
              <input type="text" placeholder="Enter promo code" />
              <button>Apply</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Cart;
